import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download } from "lucide-react";
import { PrimaryCTA } from "@/components/revanche/PrimaryCTA";
import { ReportFrame } from "@/components/revanche/ReportFrame";
import { OfferSheet } from "@/components/revanche/OfferSheet";
import { BottomSheet } from "@/components/revanche/BottomSheet";
import { ReferralPopup } from "@/components/revanche/ReferralPopup";
import { SubNav } from "@/components/revanche/SubNav";
import { canShowReferral } from "@/lib/referral";
import { getSessionId } from "@/lib/api";
import { getLatestAnalysis, fetchReportHtml, reportDownloadUrl } from "@/lib/pay";

const MASK = ["8", "1", "7"];

const Value = ({ label, value, locked, idx, testId }) => (
    <div className="flex-1 text-center" data-testid={testId}>
        <div className="font-display" style={{
            fontSize: 30, fontWeight: 800, lineHeight: 1, color: "#FFFFFF",
            filter: locked ? "blur(7px)" : "blur(0px)", transition: "filter 600ms var(--ease)", userSelect: locked ? "none" : "auto",
        }}>
            {locked ? `${MASK[idx]}${MASK[(idx + 1) % 3]}` : value}
        </div>
        <div className="font-body mt-1" style={{ fontSize: 12, color: "var(--text-secondary)" }}>{label}</div>
    </div>
);

const Preview = () => {
    const navigate = useNavigate();
    const sid = getSessionId();
    const backend = process.env.REACT_APP_BACKEND_URL;
    const [analysis, setAnalysis] = useState(JSON.parse(localStorage.getItem("revanche_preview") || localStorage.getItem("revanche_analysis") || "{}"));
    const [answers, setAnswers] = useState(JSON.parse(localStorage.getItem("revanche_answers") || "{}"));
    const [locked, setLocked] = useState(true);
    const [html, setHtml] = useState("");
    const [offerOpen, setOfferOpen] = useState(false);
    const [mikeOpen, setMikeOpen] = useState(false);
    const [refOpen, setRefOpen] = useState(false);
    const [photoOk, setPhotoOk] = useState(true);
    const revealed = useRef(false);

    const onDownload = () => {
        try {
            if (!localStorage.getItem("revanche_first_report_dl")) {
                localStorage.setItem("revanche_first_report_dl", "1");
                if (canShowReferral()) setTimeout(() => setRefOpen(true), 1500);
            }
        } catch (e) {}
    };

    useEffect(() => {
        if (!sid) return undefined;
        let cancelled = false;
        // Avant paiement : AUCUN appel IA, aucun fetch du rapport. On garde l'aperçu modèle
        // (titre + scores masqués + rapport flouté générique). Après paiement : on affiche la VRAIE analyse.
        const load = async () => {
            try {
                const r = await getLatestAnalysis();
                if (cancelled) return;
                if (r.answers) setAnswers((a) => ({ ...r.answers, ...a }));
                if (r.unlocked) {
                    revealed.current = true;
                    setLocked(false);
                    if (r.analysis && Object.keys(r.analysis).length) setAnalysis(r.analysis);
                    fetchReportHtml().then((h) => { if (!cancelled) setHtml(h); }).catch(() => {});
                    if (localStorage.getItem("revanche_just_paid") === "1") {
                        localStorage.removeItem("revanche_just_paid");
                        setTimeout(() => { if (!cancelled) setMikeOpen(true); }, 3000);
                    }
                }
            } catch (e) { /* pas encore d'analyse débloquée : on reste sur l'aperçu modèle */ }
        };
        load();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const reveal = async () => {
        if (revealed.current) return;
        revealed.current = true;
        setOfferOpen(false);
        // Après paiement : on génère la VRAIE analyse (photos + réponses déjà enregistrées) via
        // l'écran de chargement soigné, puis on revient sur l'aperçu débloqué.
        localStorage.setItem("revanche_postpay", "1");
        localStorage.setItem("revanche_just_paid", "1");
        navigate("/analyse-en-cours");
    };

    const prenom = (answers.prenom || "").trim();
    const score = analysis.score_attirance ?? 0;
    const pot = analysis.potentiel ?? 0;
    const type = analysis.type || {};

    // La fenêtre d'offre s'ouvre seule 3 s après l'arrivée sur l'aperçu (mode verrouillé).
    useEffect(() => {
        if (locked && !revealed.current) {
            const t = setTimeout(() => setOfferOpen(true), 3000);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [locked]);

    return (
        <div className="fixed inset-0 bg-black max-w-[480px] mx-auto flex flex-col overflow-hidden" data-testid="preview-screen">
            {/* En-tête : photo, prénom, score, titre de profil */}
            <div className="shrink-0 safe-top px-5 pt-3 relative">
                {locked && (
                    <button data-testid="ready-banner" onClick={() => setOfferOpen(true)}
                        className="tap-feedback w-full rounded-xl mb-2 px-4 py-2.5 flex items-center justify-between"
                        style={{ border: "1px solid var(--red)", background: "rgba(225,6,0,0.10)" }}>
                        <span className="font-body text-white" style={{ fontSize: 14 }}>Ton analyse est prête. Débloque-la.</span>
                        <span className="font-display font-black uppercase text-revred" style={{ fontSize: 13 }}>Débloquer</span>
                    </button>
                )}
                <div className="flex justify-end" style={{ height: 30 }}>
                    {!locked && (
                        <a
                            href={reportDownloadUrl()}
                            download
                            onClick={onDownload}
                            data-testid="btn-download-report"
                            className="tap-feedback flex items-center gap-1.5 font-body rounded-full"
                            style={{ fontSize: 12, color: "var(--text-secondary)", border: "1px solid var(--border)", padding: "0 12px", height: 30 }}
                        >
                            <Download size={14} strokeWidth={1.5} /> Télécharger mon analyse
                        </a>
                    )}
                </div>
                <div className="flex flex-col items-center">
                    {photoOk ? (
                        <img
                            src={`${backend}/api/session/${sid}/photo/face`}
                            onError={() => setPhotoOk(false)}
                            alt=""
                            data-testid="preview-photo"
                            data-clarity-mask="true"
                            className="rounded-full object-cover"
                            style={{ width: 64, height: 64, border: "1px solid var(--border)" }}
                        />
                    ) : (
                        <div className="rounded-full" style={{ width: 64, height: 64, backgroundColor: "var(--surface-2)", border: "1px solid var(--border)" }} />
                    )}
                    <p className="font-body mt-2 text-white" style={{ fontSize: 14 }} data-testid="preview-prenom">{prenom}</p>
                    <p data-testid="preview-score" data-clarity-mask="true" className="font-display" style={{
                        fontSize: 96, fontWeight: 800, lineHeight: 0.9, color: "var(--red)",
                        filter: locked ? "blur(14px)" : "blur(0px)", transition: "filter 600ms var(--ease)",
                        userSelect: locked ? "none" : "auto",
                    }}>
                        {locked ? "82" : score}<span style={{ fontSize: 40, color: "#FFFFFF" }}>%</span>
                    </p>
                    {type.nom ? (
                        <p className="font-body font-semibold text-white mt-1" style={{ fontSize: 18 }} data-testid="preview-type">{type.nom}</p>
                    ) : null}
                </div>

                <div className="flex items-start gap-2 mt-4 pb-4" style={{ borderBottom: "1px solid var(--border)" }} data-testid="preview-values">
                    <Value label="Potentiel" value={`${pot}%`} locked={locked} idx={0} testId="value-potentiel" />
                    <Value label="Points à gagner" value={`+${Math.max(0, pot - score)}`} locked={locked} idx={1} testId="value-ecart" />
                    <Value label="Masculinité" value={`${analysis.score_masculinite ?? "—"}`} locked={locked} idx={2} testId="value-masculinite" />
                </div>
            </div>

            {/* Le vrai rapport, flouté tant que verrouillé */}
            <div className="flex-1 min-h-0 relative">
                <ReportFrame html={html} locked={locked} />
                {locked && (
                    <div className="absolute inset-0" data-testid="unlock-banner">
                        <div className="absolute inset-x-0 bottom-0 px-5 pt-20 pb-6 safe-bottom"
                            style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, #000 45%)" }}>
                            <button
                                data-testid="btn-unlock"
                                onClick={() => setOfferOpen(true)}
                                className="tap-feedback w-full rounded-full font-display font-black uppercase text-white"
                                style={{ height: 56, background: "var(--red)", fontSize: 16, letterSpacing: "0.02em" }}
                            >
                                Appuie pour voir ton analyse complète
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <OfferSheet open={offerOpen} onClose={() => setOfferOpen(false)} onPaid={reveal} />

            <BottomSheet open={mikeOpen} onClose={() => setMikeOpen(false)} height="auto" testId="mike-sheet">
                <div className="px-6 pt-3 pb-6">
                    <h2 className="font-body font-bold text-white" style={{ fontSize: 22 }}>Une question sur ton analyse&nbsp;?</h2>
                    <p className="font-body mt-2" style={{ fontSize: 16, color: "var(--text-secondary)" }}>Mike a déjà lu tes résultats.</p>
                    <div className="mt-6">
                        <PrimaryCTA dataTestId="btn-talk-mike" onClick={() => navigate("/coach")}>Parler à Mike</PrimaryCTA>
                    </div>
                    <button data-testid="btn-mike-later" onClick={() => setMikeOpen(false)}
                        className="tap-feedback w-full mt-3 font-body" style={{ height: 44, fontSize: 15, color: "var(--text-secondary)" }}>
                        Plus tard
                    </button>
                </div>
            </BottomSheet>

            <ReferralPopup open={refOpen} onClose={() => setRefOpen(false)} />
            <SubNav />
        </div>
    );
};

export default Preview;
